var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as pc from "playcanvas";
import { bleedIntoTransparentPixels } from "3d/bleed-transparent-pixels";
import { setEmphasizedVariant } from "3d/material-effects";
var CAMERA_ENTITY_NAME = "Camera";
/* Decoded icons by image URL. The placeholders draw the same few images over
 * and over, so after the first one a new icon gets its texture at once. */
var iconPixelsCache = new Map();
var loadIconPixels = function (imageUrl) {
    var cached = iconPixelsCache.get(imageUrl);
    if (cached)
        return cached;
    var pixels = new Promise(function (resolve, reject) {
        var image = new Image();
        image.onload = function () {
            var width = image.width, height = image.height;
            var canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            var context = canvas.getContext("2d");
            if (!context) {
                reject(new Error("No 2D canvas context to decode an icon with"));
                return;
            }
            context.drawImage(image, 0, 0);
            var data = context.getImageData(0, 0, width, height).data;
            bleedIntoTransparentPixels(data, width, height);
            resolve({ data: data, width: width, height: height });
        };
        image.onerror = function () { return reject(new Error("Could not load icon ".concat(imageUrl))); };
        image.src = imageUrl;
    });
    // A failed load is not kept, so the next icon tries again.
    pixels.catch(function () { return iconPixelsCache.delete(imageUrl); });
    iconPixelsCache.set(imageUrl, pixels);
    return pixels;
};
/* Unlit and without the scene's tone mapping and gamma: the icon is drawn
 * through the emissive channel so its colours on screen are exactly the
 * image's, whatever the scene lighting and exposure do to the models around
 * it. `fadeMaterial` works through the opacity as usual. */
var createIconMaterial = function (app, imageUrl) {
    var material = new pc.StandardMaterial();
    material.diffuse = new pc.Color(0, 0, 0);
    material.emissive = new pc.Color(1, 1, 1);
    material.emissiveTint = true;
    material.useLighting = false;
    material.useSkybox = false;
    material.useGammaTonemap = false;
    material.opacityMapChannel = "a";
    material.blendType = pc.BLEND_NORMAL;
    material.cull = pc.CULLFACE_NONE;
    /* Invisible until the image is in: without it the plane is a white
     * square. */
    material.opacity = 0;
    material.update();
    var texture;
    var disposed = false;
    loadIconPixels(imageUrl)
        .then(function (_a) {
        var data = _a.data, width = _a.width, height = _a.height;
        if (disposed)
            return;
        /* Written into the texture directly: going back through a canvas
         * would premultiply the bled colours away again. */
        texture = new pc.Texture(app.graphicsDevice, {
            width: width,
            height: height,
            format: pc.PIXELFORMAT_R8_G8_B8_A8,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        texture.lock().set(data);
        texture.unlock();
        material.emissiveMap = texture;
        material.opacityMap = texture;
        material.opacity = 1;
        material.update();
    })
        .catch(function (error) { return console.error(error); });
    return {
        material: material,
        destroy: function () {
            disposed = true;
            material.destroy();
            texture === null || texture === void 0 ? void 0 : texture.destroy();
        },
    };
};
/**
 * A flat square, face up (+Y), that shows a 2D icon on it, for marking a spot
 * in the scene without a 3D model. It is an ordinary mesh so the picker,
 * hover/selection fades (`material-effects`) and bounding boxes treat it like
 * any model. The square is centred on the entity's origin; place it with
 * `entity.setLocalPosition`.
 */
export var createIconPlane = function (app, _a) {
    var name = _a.name, imageUrl = _a.imageUrl, emphasizedImageUrl = _a.emphasizedImageUrl, size = _a.size;
    var icon = createIconMaterial(app, imageUrl);
    var emphasizedIcon = emphasizedImageUrl && createIconMaterial(app, emphasizedImageUrl);
    if (emphasizedIcon) {
        setEmphasizedVariant(icon.material, emphasizedIcon.material);
    }
    var entity = new pc.Entity(name);
    entity.addComponent("model", { type: "plane" });
    if (entity.model)
        entity.model.material = icon.material;
    entity.setLocalScale(size, 1, size);
    return {
        entity: entity,
        destroy: function () {
            entity.destroy();
            icon.destroy();
            if (emphasizedIcon)
                emphasizedIcon.destroy();
        },
    };
};
/**
 * How much bigger an icon at `position` must be to look as big as it would
 * lying at `referenceHeight` straight below (or above) it: the ratio of their
 * depths along the camera's view direction, which is what perspective divides
 * sizes by. `undefined` while either point is not in front of the camera.
 */
export var sizeScaleAsIfAtHeight = function (position, referenceHeight, cameraPosition, cameraForward) {
    var depthAt = function (y) {
        return (position.x - cameraPosition.x) * cameraForward.x +
            (y - cameraPosition.y) * cameraForward.y +
            (position.z - cameraPosition.z) * cameraForward.z;
    };
    var depth = depthAt(position.y);
    var referenceDepth = depthAt(referenceHeight);
    if (depth <= 0 || referenceDepth <= 0)
        return undefined;
    return depth / referenceDepth;
};
/**
 * An icon plane (`createIconPlane`) that always faces the camera.
 */
export var createIconBillboard = function (app, _a) {
    var sizeAsIfAtHeight = _a.sizeAsIfAtHeight, options = __rest(_a, ["sizeAsIfAtHeight"]);
    var _b = createIconPlane(app, options), entity = _b.entity, destroy = _b.destroy;
    /* Screen-aligned: copy the camera's rotation, then tip the plane (whose
     * face is +Y) up so its face points back at the viewer (+Z of the camera).
     * Copying the rotation rather than `lookAt` keeps the icon upright and
     * works with the top-down edit view, where `lookAt`'s up vector degenerates. */
    var faceCamera = function () {
        var camera = app.root.findByName(CAMERA_ENTITY_NAME);
        if (!camera)
            return;
        entity.setRotation(camera.getRotation());
        entity.rotateLocal(90, 0, 0);
        if (sizeAsIfAtHeight === undefined)
            return;
        var scale = sizeScaleAsIfAtHeight(entity.getPosition(), sizeAsIfAtHeight, camera.getPosition(), camera.forward);
        if (scale === undefined)
            return;
        entity.setLocalScale(options.size * scale, 1, options.size * scale);
    };
    faceCamera();
    app.on("update", faceCamera);
    return {
        entity: entity,
        destroy: function () {
            app.off("update", faceCamera);
            destroy();
        },
    };
};
